/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import sortBy from 'lodash-es/sortBy';
import { action } from '@ember/object';
import groupBy from 'lodash-es/groupBy';
import { type Registry as Services, service } from '@ember/service';
import PhDoorOpen from 'ember-phosphor-icons/components/ph-door-open';
import PhCalendarBlank from 'ember-phosphor-icons/components/ph-calendar-blank';
import PhTrayArrowDown from 'ember-phosphor-icons/components/ph-tray-arrow-down';
import ClientModel from 'uplisting-frontend/models/client';
import TeammateModel from 'uplisting-frontend/models/teammate';
import PermissionModel from 'uplisting-frontend/models/permission';

const ICON_FOR_CATEGORY = {
  Insights: 'chart-mixed',
  Reports: 'file',
  Action: 'circle-check',
  Automations: 'shuffle',
  'Clients & Statements': 'address-card',
  'Direct Booking Engine': 'bullseye-pointer',
  Upsells: 'arrow-trend-up',
};

type PermissionsParentRecord = ClientModel | TeammateModel;

interface IArgs {
  record: PermissionsParentRecord;
  successNotificationKey?: string;
}

interface PermissionsSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export type PermissionsChildrenGrouped = Record<string, PermissionModel[]>;

interface IPermissionsGrouped {
  category: string;
  subCategories: PermissionsChildrenGrouped;
}

function sortPermissions(
  parents: PermissionModel[],
): PermissionsChildrenGrouped {
  const grouped = groupBy(parents, 'subCategory');
  const sorted = Object.entries(grouped).sort();

  return Object.fromEntries(sorted) as PermissionsChildrenGrouped;
}

export default class UiPermissionsComponent extends Component<PermissionsSignature> {
  @service notifications!: Services['notifications'];

  @cached
  get successNotificationKey(): string {
    return this.args.successNotificationKey ?? 'notifications.applied';
  }

  @cached
  get parentPermissions(): PermissionModel[] {
    return sortBy(
      this.args.record.permissions.filter((item) => !item.parent),
      'category',
    );
  }

  @cached
  get parentsByCategory(): IPermissionsGrouped[] {
    const grouped = groupBy(this.parentPermissions, 'category');

    return Object.entries(grouped).map(([category, parents]) => ({
      category,
      subCategories: sortPermissions(parents as PermissionModel[]),
    }));
  }

  @action
  hasFaIconForCategory(category: string): string | false {
    return ICON_FOR_CATEGORY[category] || false;
  }

  @action
  computeIconComponentFor(
    category: string,
  ):
    | typeof PhDoorOpen
    | typeof PhCalendarBlank
    | typeof PhTrayArrowDown
    | undefined {
    switch (category) {
      case 'Account Settings':
        return PhDoorOpen;
      case 'Calendar':
        return PhCalendarBlank;
      case 'Inbox':
        return PhTrayArrowDown;
    }
  }

  @action
  async handleUpdatePermission(item: PermissionModel): Promise<void> {
    const isPermitted = item.permitted;

    try {
      item.permitted = !item.permitted;

      await item.save();

      this.notifications.info(this.successNotificationKey);
    } catch {
      item.permitted = isPermitted;

      this.notifications.error();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Permissions': typeof UiPermissionsComponent;
  }
}
